import React, { useEffect, useMemo, useRef } from 'react';
import { normalizeFieldKey } from '../../create-template/extensions/fieldTokenEditorUtils';
import { cn } from '../../../utils/cn';
import { formatDateBr } from '../../../utils/dateBr';
import { scrollContainerToChild } from '../utils/scrollContainerToChild';

const previewFieldHighlightClass =
  'rounded-sm bg-[rgb(186,213,254)]/85 dark:bg-blue-950/55 ring-1 ring-inset ring-blue-400/35 shadow-sm transition-[background-color,box-shadow] duration-200';

const alignClass = (align) => {
  if (align === 'center') return 'text-center';
  if (align === 'right') return 'text-right';
  if (align === 'justify') return 'text-justify';
  return 'text-left';
};

function applyMarks(text, marks = []) {
  let node = text;
  (marks ?? []).forEach((mark) => {
    if (mark.type === 'textStyle') {
      const style = {};
      if (mark.attrs?.fontSize) style.fontSize = mark.attrs.fontSize;
      if (mark.attrs?.fontFamily) style.fontFamily = mark.attrs.fontFamily;
      if (mark.attrs?.color) style.color = mark.attrs.color;
      if (mark.attrs?.backgroundColor) {
        style.backgroundColor = mark.attrs.backgroundColor;
      }
      if (mark.attrs?.lineHeight) style.lineHeight = mark.attrs.lineHeight;
      if (Object.keys(style).length > 0) {
        node = <span style={style}>{node}</span>;
      }
    }
    if (mark.type === 'bold') node = <strong>{node}</strong>;
    if (mark.type === 'italic') node = <em>{node}</em>;
    if (mark.type === 'underline') node = <u>{node}</u>;
    if (mark.type === 'strike') node = <s>{node}</s>;
    if (mark.type === 'link' && mark.attrs?.href) {
      node = (
        <a href={mark.attrs.href} className="text-primary underline underline-offset-2">
          {node}
        </a>
      );
    }
  });
  return node;
}

function isEmptyInlineContent(nodes) {
  if (!Array.isArray(nodes) || nodes.length === 0) return true;
  return nodes.every((node) => {
    if (node.type === 'text') return !String(node.text ?? '').length;
    if (node.type === 'hardBreak') return false;
    if (Array.isArray(node.content)) return isEmptyInlineContent(node.content);
    return false;
  });
}

/** Empty TipTap paragraphs need visible height (same as editor blank lines). */
function renderParagraphPlaceholder() {
  return '\u00A0';
}

const PersonalizedLiveContractPreview = ({
  documentContent,
  formData,
  formFields = [],
  scrollContainerRef = null,
  scrollToPlaceholder = null,
  highlightedPlaceholder = null,
  onScrollDone,
}) => {
  const containerRef = useRef(null);

  const dateFieldIds = useMemo(
    () =>
      new Set(
        (formFields ?? [])
          .filter((field) => field.type === 'date')
          .map((field) => field.id)
      ),
    [formFields]
  );

  useEffect(() => {
    if (!scrollToPlaceholder || !containerRef.current) return undefined;

    const scrollRoot = scrollContainerRef?.current;
    if (!scrollRoot) return undefined;

    const timer = requestAnimationFrame(() => {
      const el = containerRef.current?.querySelector(
        `[data-placeholder-key="${CSS.escape(scrollToPlaceholder)}"]`
      );
      if (el) {
        scrollContainerToChild(scrollRoot, el);
      }
      onScrollDone?.();
    });

    return () => cancelAnimationFrame(timer);
  }, [scrollToPlaceholder, scrollContainerRef, onScrollDone]);

  const resolveFieldValue = (key) => {
    const normalized = normalizeFieldKey(key);
    if (!normalized) return '';
    const value = formData[normalized];
    if (value === null || value === undefined || value === '') return '';
    if (dateFieldIds.has(normalized)) {
      return formatDateBr(value);
    }
    return String(value);
  };

  const renderInlineContent = (nodes, keyPrefix = 'inline') => {
    if (!Array.isArray(nodes) || nodes.length === 0) return null;

    return nodes.map((node, index) => {
      const key = `${keyPrefix}-${index}`;

      if (node.type === 'text') {
        return (
          <React.Fragment key={key}>
            {applyMarks(node.text ?? '', node.marks)}
          </React.Fragment>
        );
      }

      if (node.type === 'hardBreak') {
        return <br key={key} />;
      }

      if (node.type === 'fieldToken') {
        const fieldKey = normalizeFieldKey(node.attrs?.key);
        const isHighlighted =
          Boolean(highlightedPlaceholder) && highlightedPlaceholder === fieldKey;
        const value = resolveFieldValue(fieldKey);

        return (
          <span
            key={key}
            data-placeholder-key={fieldKey}
            className={cn(isHighlighted && previewFieldHighlightClass)}
          >
            {value || `{${fieldKey}}`}
          </span>
        );
      }

      if (Array.isArray(node.content)) {
        return (
          <React.Fragment key={key}>{renderInlineContent(node.content, key)}</React.Fragment>
        );
      }

      return null;
    });
  };

  const renderBlockNodes = (nodes, keyPrefix = 'block') => {
    if (!Array.isArray(nodes)) return null;

    return nodes.map((node, index) => {
      const key = `${keyPrefix}-${index}`;

      if (node.type === 'paragraph') {
        const inline = renderInlineContent(node.content, key);
        const isEmpty = inline == null || isEmptyInlineContent(node.content);

        return (
          <p
            key={key}
            className={cn('whitespace-pre-wrap', alignClass(node.attrs?.textAlign))}
          >
            {isEmpty ? renderParagraphPlaceholder() : inline}
          </p>
        );
      }

      if (node.type === 'heading') {
        const level = node.attrs?.level ?? 2;
        const Tag = level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3';
        const inline = renderInlineContent(node.content, key);
        const isEmpty = inline == null || isEmptyInlineContent(node.content);

        return (
          <Tag
            key={key}
            className={alignClass(node.attrs?.textAlign)}
          >
            {isEmpty ? renderParagraphPlaceholder() : inline}
          </Tag>
        );
      }

      if (node.type === 'bulletList') {
        return (
          <ul key={key} className="list-disc pl-6">
            {renderListItems(node.content, key)}
          </ul>
        );
      }

      if (node.type === 'orderedList') {
        return (
          <ol key={key} className="list-decimal pl-6 personalized-preview-ol">
            {renderListItems(node.content, key)}
          </ol>
        );
      }

      if (node.type === 'blockquote') {
        return (
          <blockquote key={key} className="border-l-4 border-primary/40 pl-4 text-muted-foreground italic">
            {renderBlockNodes(node.content, key)}
          </blockquote>
        );
      }

      if (node.type === 'horizontalRule') {
        return <hr key={key} className="my-4 border-border" />;
      }

      if (node.type === 'listItem') {
        return (
          <li key={key} className="space-y-1">
            {renderBlockNodes(node.content, key)}
          </li>
        );
      }

      if (Array.isArray(node.content)) {
        return (
          <div key={key}>{renderBlockNodes(node.content, key)}</div>
        );
      }

      return null;
    });
  };

  const renderListItems = (nodes, keyPrefix) => {
    if (!Array.isArray(nodes)) return null;
    return nodes.map((node, index) => {
      if (node.type !== 'listItem') return null;
      return (
        <li key={`${keyPrefix}-li-${index}`} className="space-y-1">
          {renderBlockNodes(node.content, `${keyPrefix}-li-${index}`)}
        </li>
      );
    });
  };

  if (!documentContent?.content?.length) {
    return (
      <div className="bg-white p-10 shadow rounded text-sm leading-relaxed max-w-3xl mx-auto text-muted-foreground">
        Selecione um modelo personalizado para visualizar a prévia do documento.
      </div>
    );
  }

  return (
    <>
      <style>{`
        .personalized-contract-preview {
          line-height: 1.65;
        }
        .personalized-contract-preview p {
          margin: 0.35em 0;
          min-height: 1.65em;
        }
        .personalized-contract-preview h1,
        .personalized-contract-preview h2,
        .personalized-contract-preview h3 {
          font-weight: 700;
          line-height: 1.25;
          margin: 0.75em 0 0.35em;
        }
        .personalized-contract-preview h1 { font-size: 1.75rem; }
        .personalized-contract-preview h2 { font-size: 1.35rem; }
        .personalized-contract-preview h3 { font-size: 1.15rem; }
        .personalized-contract-preview ul,
        .personalized-contract-preview ol {
          margin: 0.35em 0;
          padding-left: 1.5rem;
        }
        .personalized-contract-preview li {
          margin: 0.2em 0;
        }
        .personalized-contract-preview blockquote {
          margin: 0.5em 0;
        }
        .personalized-contract-preview hr {
          margin: 1rem 0;
          border: none;
          border-top: 1px solid hsl(var(--border));
        }
        .personalized-contract-preview ol ol { list-style-type: lower-alpha; }
        .personalized-contract-preview ol ol ol { list-style-type: lower-roman; }
      `}</style>
      <div
        ref={containerRef}
        className="personalized-contract-preview bg-white p-10 shadow rounded text-base leading-relaxed max-w-3xl mx-auto text-foreground"
      >
        {renderBlockNodes(documentContent.content)}
      </div>
    </>
  );
};

export default PersonalizedLiveContractPreview;
